"use client";

import { DataSkeleton } from '@/app/Components/DataSkeleton';
import {
  CalendarDaysIcon,
  BuildingOffice2Icon,
  FunnelIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import { Button } from "@/app/Components/Button";
import { buttonClassName } from "@/app/Components/buttonStyles";
import { Field, Input, Select } from "@/app/Components/Field";
import { InfoTooltip } from "@/app/Components/InfoTooltip";
import {
  OperationalEvidence,
  OperationalPage,
  OperationalSection,
  OperationalSource,
  OperationalState,
} from "@/app/Components/OperationalPage";
import { Sheet } from "@/app/Components/Sheet";
import { Tabs } from "@/app/Components/Tabs";
import type { RouteDataState } from "@/app/lib/routeStateContract";
import type { CampusEvent, EventFilters } from "@/app/types/events";
import EventRow from "./components/EventRow";
import EventSidebar from "./components/EventSidebar";
import EventsIntramuralPanel from "./components/EventsIntramuralPanel";
import type { IntramuralGame } from "./components/EventsIntramuralPanel";
import SportsRow from "./components/SportsRow";
import StudySpaceRow from "./components/StudySpaceRow";
import {
  ALL_EVENT_SOURCES,
  DATE_RANGE_OPTIONS,
  EVENT_TABS,
  eventEmptyCopy,
  eventSummaryLabel,
  INTRAMURAL_SPORTS,
  EVENT_SPORTS,
  type EventTab,
  type SportItem,
} from "./eventsDirectoryViewModel";

const tabIcons = {
  clubs: <CalendarDaysIcon className="h-4 w-4" />,
  rutgers: <TrophyIcon className="h-4 w-4" />,
};
export type EventsDirectoryViewProps = {
  embedded?: boolean;
  events: CampusEvent[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number | null;
  activeTab: EventTab;
  activeSport: string | null;
  filters: EventFilters;
  showMobileFilters: boolean;
  observerTarget: React.RefObject<HTMLDivElement | null>;
  onSelectTab: (tab: EventTab) => void;
  onSelectSport: (tab: EventTab, sport: SportItem) => void;
  onSetFilters: (
    next: EventFilters | ((previous: EventFilters) => EventFilters),
  ) => void;
  onSearchChange: (search: string) => void;
  onOpenFilters: () => void;
  onCloseFilters: () => void;
  onRetry: () => void;
  fixtureState?: RouteDataState;
  intramuralFixtureGames?: IntramuralGame[];
};

const retainedFixtureStateCopy: Partial<
  Record<RouteDataState, { title: string; description: string }>
> = {
  refreshing: {
    title: "Refreshing events",
    description: "Earlier results remain available while sources refresh.",
  },
  "stale-cache": {
    title: "Cached events may be out of date",
    description: "Confirm details with the event organizer.",
  },
  partial: {
    title: "Some event sources are unavailable",
    description:
      "Available results are shown below.",
  },
  permission: {
    title: "Sign in with the event organizer",
    description: "Public listings remain available. Open the source for restricted details.",
  },
};
function renderEventRow(event: CampusEvent, activeTab: EventTab) {
  const category = event.category?.toLowerCase() ?? "";
  if (activeTab === "rooms" && category.includes("study space"))
    return <StudySpaceRow key={event.id} event={event} />;
  if (
    event.source === "athletics" ||
    [
      "basketball",
      "soccer",
      "football",
      "softball",
      "baseball",
      "cricket",
      "lacrosse",
      "volleyball",
    ].some((sport) => category.includes(sport)) ||
    category.includes("intramural")
  )
    return <SportsRow key={event.id} event={event} />;
  return <EventRow key={event.id} event={event} />;
}

function EventsBrowseControls({
  activeTab,
  onSelectTab,
}: Pick<
  EventsDirectoryViewProps,
  "activeTab" | "onSelectTab"
>) {
  return (
    <section aria-label="Event directory controls">
      <Tabs
        label="Event directories"
        items={EVENT_TABS.map((tab) => ({
          ...tab,
          icon: tabIcons[tab.id],
        }))}
        activeId={activeTab === "intramural" ? "rutgers" : activeTab === "rooms" ? "clubs" : activeTab}
        onChange={(tab) => onSelectTab(tab as EventTab)}
      />
    </section>
  );
}

function EventsResults({
  activeTab,
  error,
  events,
  filters,
  fixtureState,
  hasMore,
  loading,
  observerTarget,
  onRetry,
  onSelectTab,
  summary,
  totalCount,
}: Pick<
  EventsDirectoryViewProps,
  | "activeTab"
  | "error"
  | "events"
  | "filters"
  | "fixtureState"
  | "hasMore"
  | "loading"
  | "observerTarget"
  | "onRetry"
  | "onSelectTab"
  | "totalCount"
> & { summary: string }) {
  const hasActiveFilters =
    filters.sources.length > 0 ||
    filters.search !== "" ||
    filters.category !== null;
  const empty = eventEmptyCopy(activeTab, hasActiveFilters);
  return (
    <>
      {error ? (
        <OperationalState
          kind={fixtureState === "hard-failure" ? "hard-failure" : "error"}
          title={
            events.length ? "More results unavailable" : "Feed unavailable"
          }
          description={error}
          action={
            <Button variant="danger" onClick={onRetry}>
              Retry
            </Button>
          }
        />
      ) : null}
      <OperationalSection
        title={activeTab === "rooms" ? "Study spaces" : "Results"}
        description={
          totalCount !== null
            ? `${totalCount.toLocaleString()} matching results`
            : undefined
        }
      >
        <div
          role="tabpanel"
          aria-label={`${summary} results`}
          className="overflow-hidden"
        >
          {events.map((event) => renderEventRow(event, activeTab))}
          {loading ? (
            <DataSkeleton label={events.length ? 'Loading more results' : `Loading ${activeTab === "rooms" ? "study spaces" : "campus events"}`} count={events.length ? 1 : 3} />
          ) : null}
          {!loading &&
          !error &&
          events.length === 0 &&
          fixtureState !== "permission" ? (
            <OperationalState
              kind="empty"
              title={empty.title}
              description={empty.description}
              action={
                hasActiveFilters ? (
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="primary"
                      onClick={() => onSelectTab("clubs")}
                    >
                      Reset filters
                    </Button>
                  </div>
                ) : undefined
              }
            />
          ) : null}
          <div ref={observerTarget} className="h-4" aria-hidden="true" />
          {!hasMore && !loading && events.length > 0 && !error ? (
            <p className="py-3 text-center text-xs text-[var(--ss-text-muted)]">
              End of results
            </p>
          ) : null}
        </div>
      </OperationalSection>
    </>
  );
}

function EventsProvenance({
  activeTab,
  fixtureState,
}: Pick<EventsDirectoryViewProps, "activeTab" | "fixtureState">) {
  const stale =
    fixtureState === "refreshing" ||
    fixtureState === "stale-cache" ||
    fixtureState === "partial";
  return activeTab === "rooms" ? (
    <OperationalSource
      kind="checked-in"
      source="Spring 2026 room-availability snapshot"
      stale
    />
  ) : (
    <OperationalSource
      kind="cached"
      source="Rutgers Events service · response cache up to 5 minutes"
      stale={stale}
    />
  );
}

export function EventsDirectoryView(props: EventsDirectoryViewProps) {
  const {
    activeTab,
    activeSport,
    error,
    events,
    filters,
    fixtureState,
    embedded = false,
    intramuralFixtureGames,
    hasMore,
    loading,
    observerTarget,
    onCloseFilters,
    onOpenFilters,
    onRetry,
    onSearchChange,
    onSelectSport,
    onSelectTab,
    onSetFilters,
    showMobileFilters,
    totalCount,
  } = props;
  const summary = eventSummaryLabel(activeTab);
  const sports = activeTab === "rutgers" ? EVENT_SPORTS : activeTab === "intramural" ? INTRAMURAL_SPORTS : [];
  const fixtureNotice = fixtureState
    ? retainedFixtureStateCopy[fixtureState]
    : undefined;
  return (
    <OperationalPage className={embedded ? "max-w-none p-0" : "pb-mobile-nav-clearance lg:pb-12"}>
        <div className="min-w-0 space-y-4">
          {!embedded ? <header className="flex items-center justify-between gap-3">
            <h1 className="operational-title">Events</h1>
            <InfoTooltip title="Event sources" text="Service responses may be cached for up to five minutes; this does not show when organizers last updated their listings. Athletics times are shown only when ESPN confirms them; otherwise check the official schedule. Open the organizer's details to confirm cost, accessibility, and registration before attending." />
          </header> : null}
          {fixtureNotice ? (
            <OperationalState
              kind={
                fixtureState as
                  "refreshing" | "stale-cache" | "partial" | "permission"
              }
              title={fixtureNotice.title}
              description={fixtureNotice.description}
            />
          ) : null}
          <EventsBrowseControls
            activeTab={activeTab}
            onSelectTab={onSelectTab}
          />
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(10rem,13rem)_auto]" aria-label="Search and filter events">
            <Field label="Search events" className="col-span-2 sm:col-span-1">
              {(fieldProps) => <Input {...fieldProps} type="search" placeholder="Name, interest, or place" value={filters.search} onChange={(event) => onSearchChange(event.target.value)} />}
            </Field>
            <Field label="When">
              {(fieldProps) => <Select {...fieldProps} value={filters.dateRange} onChange={(event) => onSetFilters((current) => ({ ...current, dateRange: event.target.value as EventFilters["dateRange"] }))}>
                {DATE_RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>}
            </Field>
            <Button variant="secondary" onClick={onOpenFilters} aria-label="Open filters">
              <FunnelIcon aria-hidden="true" className="h-4 w-4" />
              Filters
            </Button>
          </div>
          {activeTab === "rooms" ? <OperationalState kind="stale-cache" title="Spring 2026 room schedule" description="Historical timetable. Check Rutgers booking for current availability." /> : null}
          <EventsResults
            activeTab={activeTab}
            error={error}
            events={events}
            filters={filters}
            fixtureState={fixtureState}
            hasMore={hasMore}
            loading={loading}
            observerTarget={observerTarget}
            onRetry={onRetry}
            onSelectTab={onSelectTab}
            summary={summary}
            totalCount={totalCount}
          />
          <EventsProvenance activeTab={activeTab} fixtureState={fixtureState} />
          {activeTab === "rutgers" || activeTab === "intramural" ? (
            <EventsIntramuralPanel fixtureGames={intramuralFixtureGames} />
          ) : null}
          <Link href="/room-availability" className={buttonClassName({ variant: "quiet", size: "sm" })}>
            <BuildingOffice2Icon aria-hidden="true" className="h-4 w-4" />
            Find a study room
          </Link>
          {fixtureState === "populated" ? (
            <OperationalEvidence tone="info">
              Representative, deterministic fixture content.
            </OperationalEvidence>
          ) : null}
        </div>
      <Sheet
        isOpen={showMobileFilters}
        onClose={onCloseFilters}
        title="Filters"
        side="bottom"
      >
        {sports.length ? <Field label="Sport" className="mb-4">
          {(fieldProps) => <Select {...fieldProps} value={activeSport ?? ""} onChange={(event) => {
            const sport = sports.find((item) => item.query === event.target.value);
            if (sport) onSelectSport(activeTab, sport);
            else onSelectTab(activeTab);
          }}>
            <option value="">All sports</option>
            {sports.map((sport) => <option key={sport.query} value={sport.query}>{sport.label}</option>)}
          </Select>}
        </Field> : null}
        <EventSidebar
          filters={filters}
          setFilters={onSetFilters}
          sources={ALL_EVENT_SOURCES}
        />
        <Button className="mt-5 w-full" onClick={onCloseFilters}>
          Show results
        </Button>
      </Sheet>
    </OperationalPage>
  );
}
